import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from "react";
import { usePublishImpersonationTarget } from "@/contexts/ImpersonationTargetContext";

/**
 * Embajador impersonado, tal como lo publica el selector del portal.
 *
 * Antes aquí solo vivían `id`, `name` y `code`, y por eso el portal no podía
 * simular nada: sin `personaId` no hay expediente ni comisiones del impersonado,
 * sin `rolId` el núcleo de impersonación no puede resolver el rol efectivo
 * (`lib/impersonation/effective-identity`), y sin `authUserId` toda consulta que
 * filtre por `auth.uid()` sigue resolviendo con el admin logueado — el mismo bug
 * que ya se pagó en el Portal Agente.
 */
export interface EmbajadorImpersonationTarget {
  /** `embajadores_config.id` / id de la fila de embajador. */
  id: string;
  name: string;
  code: string | null;
  /** `personas.id` con el que se leen expediente y comisiones. */
  personaId: number | null;
  email: string | null;
  /** `usuarios.rol_id` del impersonado (para resolver permisos en vista fiel). */
  rolId: number | null;
  /** `usuarios.auth_user_id` del impersonado (para consultas por `auth.uid()`). */
  authUserId: string | null;
}

interface EmbajadorImpersonationContextType {
  impersonatedEmbajadorId: string | null;
  impersonatedEmbajadorName: string | null;
  impersonatedEmbajadorCode: string | null;
  impersonatedEmbajadorPersonaId: number | null;
  impersonatedEmbajadorEmail: string | null;
  impersonatedEmbajadorRolId: number | null;
  impersonatedEmbajadorAuthUserId: string | null;
  setImpersonatedEmbajador: (target: EmbajadorImpersonationTarget | null) => void;
  clearImpersonation: () => void;
  isImpersonating: boolean;
}

const EmbajadorImpersonationContext = createContext<EmbajadorImpersonationContextType>({
  impersonatedEmbajadorId: null,
  impersonatedEmbajadorName: null,
  impersonatedEmbajadorCode: null,
  impersonatedEmbajadorPersonaId: null,
  impersonatedEmbajadorEmail: null,
  impersonatedEmbajadorRolId: null,
  impersonatedEmbajadorAuthUserId: null,
  setImpersonatedEmbajador: () => {},
  clearImpersonation: () => {},
  isImpersonating: false,
});

export function EmbajadorImpersonationProvider({ children }: { children: ReactNode }) {
  // Se guarda el target completo en memoria (no se persiste, igual que en el
  // Portal Agente: un F5 devuelve al admin a su propia identidad).
  const [target, setTarget] = useState<EmbajadorImpersonationTarget | null>(null);

  const setImpersonatedEmbajador = useCallback(
    (next: EmbajadorImpersonationTarget | null) => setTarget(next),
    []
  );
  const clearImpersonation = useCallback(() => setTarget(null), []);

  const value = useMemo<EmbajadorImpersonationContextType>(
    () => ({
      impersonatedEmbajadorId: target?.id ?? null,
      impersonatedEmbajadorName: target?.name ?? null,
      impersonatedEmbajadorCode: target?.code ?? null,
      impersonatedEmbajadorPersonaId: target?.personaId ?? null,
      impersonatedEmbajadorEmail: target?.email ?? null,
      impersonatedEmbajadorRolId: target?.rolId ?? null,
      impersonatedEmbajadorAuthUserId: target?.authUserId ?? null,
      setImpersonatedEmbajador,
      clearImpersonation,
      isImpersonating: !!target?.id,
    }),
    [target, setImpersonatedEmbajador, clearImpersonation]
  );

  return (
    <EmbajadorImpersonationContext.Provider value={value}>
      {children}
    </EmbajadorImpersonationContext.Provider>
  );
}

export function useEmbajadorImpersonation() {
  return useContext(EmbajadorImpersonationContext);
}

/**
 * Publica el impersonado de este contexto al núcleo global (menús/rol efectivos y
 * solo lectura de toda la app). Se llama SOLO desde el layout del portal: el
 * provider vive montado en toda la app y conserva la selección, pero al salir del
 * portal el layout se desmonta, se despublica y la app deja de estar en solo
 * lectura; al volver se republica.
 */
export function usePublicarVerComoEmbajador(): void {
  const { isImpersonating, impersonatedEmbajadorEmail, impersonatedEmbajadorPersonaId, impersonatedEmbajadorName, impersonatedEmbajadorRolId } = useEmbajadorImpersonation();
  usePublishImpersonationTarget(
    isImpersonating
      ? { email: impersonatedEmbajadorEmail, personaId: impersonatedEmbajadorPersonaId, nombre: impersonatedEmbajadorName, rolId: impersonatedEmbajadorRolId }
      : null
  );
}
